import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useI18n } from '../../i18n/I18nContext';
import './AgentControlCenter.css';

// ── Tabs ──
import FraudTab from './tabs/FraudTab';
import ReportsTab from './tabs/ReportsTab';
import ProfitabilityTab from './tabs/ProfitabilityTab';
import AiPricingTab from './tabs/AiPricingTab';

// ─── icons ─────────────────────────────────────────────────────────────────
const Icon = ({ name }) => {
    const map = {
        'dashboard': 'bi bi-speedometer2',
        'chat': 'bi bi-chat-dots',
        'analyse': 'bi bi-bar-chart-line',
        'assignment': 'bi bi-person-plus',
        'incident': 'bi bi-exclamation-triangle-fill',
        'inspection': 'bi bi-clipboard-check-fill',
        'ocr': 'bi bi-card-text',
        'refresh': 'bi bi-arrow-clockwise',
        'mission': 'bi bi-truck',
        'warning': 'bi bi-exclamation-triangle',
        'spinner': 'bi bi-arrow-repeat',
        'check': 'bi bi-check-circle',
        'cross': 'bi bi-x-circle',
        'send': 'bi bi-send',
        'lightbulb': 'bi bi-lightbulb',
        'compute': 'bi bi-gear',
        'control': 'bi bi-gear-wide-connected',
        'driver': 'bi bi-person-badge',
        'shield-check': 'bi bi-shield-check',
        'file-earmark-pdf': 'bi bi-file-earmark-pdf',
        'cash-stack': 'bi bi-cash-stack',
        'tag': 'bi bi-tag'
    };
    return <i className={map[name] || `bi bi-${name}`} aria-hidden="true" />;
};

const AgentControlCenter = () => {
    const navigate = useNavigate();
    const { t } = useI18n();

    // ── Tab ────────────────────────────────────────────────────────────────
    const [tab, setTab] = useState('dashboard');

    // ── Dashboard ──────────────────────────────────────────────────────────
    const [missions, setMissions] = useState([]);
    const [requests, setRequests] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [dashLoading, setDashLoading] = useState(false);

    // ── Chat ───────────────────────────────────────────────────────────────
    const [chatMid, setChatMid] = useState('');
    const [chatQuery, setChatQuery] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef(null);

    // ── Analyse ────────────────────────────────────────────────────────────
    const [analyseMid, setAnalyseMid] = useState('');
    const [analyseResult, setAnalyseResult] = useState('');
    const [analyseLoading, setAnalyseLoading] = useState(false);

    // ── Assignment ─────────────────────────────────────────────────────────
    const [affMid, setAffMid] = useState('');
    const [affChauffeurId, setAffChauffeurId] = useState('');
    const [affProposals, setAffProposals] = useState([]);
    const [affMsg, setAffMsg] = useState('');
    const [affLoading, setAffLoading] = useState(false);

    // ── Incident ───────────────────────────────────────────────────────────
    const [incRows, setIncRows] = useState([]);
    const [incLoading, setIncLoading] = useState(false);
    const [showIncModal, setShowIncModal] = useState(false);
    const [selectedIncMid, setSelectedIncMid] = useState(null);
    const [incProposals, setIncProposals] = useState([]);
    const [isInspectionModal, setIsInspectionModal] = useState(false);

    // ── OCR ────────────────────────────────────────────────────────────────
    const [ocrStageData, setOcrStageData] = useState(null);
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrMsg, setOcrMsg] = useState('');

    // ── Reassignment Confirmation ─────────────────────────────────────────
    const [showAssignConfirm, setShowAssignConfirm] = useState(false);
    const [assignConfirmData, setAssignConfirmData] = useState(null);

    // ── Init ───────────────────────────────────────────────────────────────
    const location = useLocation();
    useEffect(() => { fetchDashboard(); }, []);
    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatHistory]);

    // Auto-trigger incident AI from notification deep-link (?incident=missionId)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const incidentMid = params.get('incident');
        const category = params.get('category');
        const chauffeurId = params.get('chauffeurId');

        if (incidentMid) {
            // ONLY trigger if this came from a manual click on the notification (triggerAI flag in state)
            const shouldTrigger = location.state?.triggerAI;

            if (shouldTrigger) {
                // Switch to incident tab and invoke the AI
                setTab('incident');
                if (category === 'CHECKLIST' || category === 'CHECK_VEHICULE') {
                    setIsInspectionModal(true);
                    runInspectionAnalysis(incidentMid, chauffeurId);
                } else {
                    setIsInspectionModal(false);
                    fetchIncProposals(incidentMid);
                }
            } else {
                // If no trigger flag, just switch to the tab without opening the modal
                setTab('incident');
            }

            // Clean the URL and state so refreshing doesn't re-trigger
            navigate(window.location.pathname, { replace: true, state: {} });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, location.state]);

    const runInspectionAnalysis = async (mid, chauffeurId) => {
        if (!mid) return;
        setIsInspectionModal(true);
        setSelectedIncMid(mid);
        setShowIncModal(true);
        setIncLoading(true);
        try {
            // Call the new agent endpoint directly or via proxy if available
            // Assuming the planningAgentService proxy exists or we use the direct agent URL if configured
            const res = await api.post('/agent/planning/inspection-check', { mid, chauffeurId });
            // For inspections, we might want to store the result differently, 
            // but for now let's reuse incProposals or a dedicated state
            // Let's wrap the string result into a "proposal" format if needed, or just show text
            setIncProposals([{
                isInspection: true,
                report: res.data?.analysis || res.data?.text || 'Rapport généré.',
                details: res.data
            }]);
        } catch (e) {
            console.error(e);
            setIncProposals([{ isInspection: true, error: "L'analyse d'inspection a échoué." }]);
        } finally {
            setIncLoading(false);
        }
    };

    const getMissionRoute = (mid) => {
        if (!mid) return '';
        // Handle encoded or literal "Général"
        if (typeof mid === 'string' && (mid.includes('Général') || mid.includes('Gnral') || mid.includes('G├®n├®ral'))) {
            return 'Incident Général';
        }
        const m = missions.find(x => String(x.id) === String(mid) || String(x.referenceNumber) === String(mid));
        return m ? `${m.departureLocation || ''} ➔ ${m.arrivalLocation || ''}` : `Mission ${mid}`;
    };

    const getMissionLabel = (mission) => {
        if (!mission) return '';
        const departureStr = mission.expediteurNom || mission.departureLocation;
        const arrivalStr = mission.destinataireNom || mission.arrivalLocation;
        let label = `${departureStr || '?'} → ${arrivalStr || '?'}`;
        if (mission.expediteurNom || mission.destinataireNom) {
            label += ` (${mission.departureLocation || '?'} → ${mission.arrivalLocation || '?'})`;
        }
        return label;
    };

    const getDriverLabel = (p) => {
        if (!p) return 'Non assigné';
        const fn = p.firstname || p.firstName || p.prenom || '';
        const ln = p.lastname || p.lastName || p.nom || '';
        const built = `${fn} ${ln}`.trim();
        return built || p.chauffeur_nom || p.full_name || p.fullName || p.chauffeurName || `Chauffeur ${p.chauffeurId || p.chauffeur_id || p.id || ''}`;
    };

    const missionIds = missions.map(m => m.id || m.referenceNumber || '').filter(Boolean);

    // ══════════════════════════════════════════════════════════════════════
    // DASHBOARD
    // ══════════════════════════════════════════════════════════════════════
    const fetchDashboard = async () => {
        setDashLoading(true);
        try {
            const res = await api.get('/agent/planning/context');
            const ctx = res.data || {};
            setMissions(ctx.activeMissions || ctx.missions || []);
            setRequests(ctx.pendingProposals || ctx.agentProposals || []);
            setChecklists(ctx.inspections || []);
        } catch (e) {
            console.error(e);
        } finally {
            setDashLoading(false);
        }
    };

    // ══════════════════════════════════════════════════════════════════════
    // CHAT
    // ══════════════════════════════════════════════════════════════════════
    const sendChat = async (e) => {
        e.preventDefault();
        if (!chatQuery.trim()) return;
        const userMsg = chatQuery;
        setChatHistory(h => [...h, { role: 'user', text: userMsg }]);
        setChatQuery('');
        setChatLoading(true);
        try {
            const res = await api.post('/agent/planning/ask', { query: userMsg, missionId: chatMid || null });
            const answer = res.data?.answer || res.data?.text || 'Pas de réponse.';
            setChatHistory(h => [...h, { role: 'agent', text: answer }]);
        } catch {
            setChatHistory(h => [...h, { role: 'agent', text: "L'agent IA est indisponible. Veuillez vérifier sa configuration." }]);
        } finally {
            setChatLoading(false);
        }
    };

    // ══════════════════════════════════════════════════════════════════════
    // ANALYSE
    // ══════════════════════════════════════════════════════════════════════

    /**
     * Reverses UTF-8 mojibake.
     * When the agent returns UTF-8 bytes but they were decoded as Latin-1, 
     * we can recover by re-encoding each char back to its byte value, then 
     * decoding as UTF-8.
     */
    const fixEncoding = (str) => {
        if (!str || typeof str !== 'string') return str;
        try {
            const bytes = new Uint8Array(str.length);
            for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i) & 0xff;
            const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
            // Only return decoded if it differs significantly (i.e. it actually fixed something)
            return decoded || str;
        } catch {
            return str;
        }
    };

    const runAnalyse = async () => {
        if (!analyseMid) return;
        setAnalyseLoading(true);
        setAnalyseResult('');
        try {
            const res = await api.post(`/agent/planning/missions/${analyseMid}/analyze`);
            const raw = res.data?.analysis || res.data?.text || 'Aucune analyse disponible.';
            setAnalyseResult(fixEncoding(raw));
        } catch {
            setAnalyseResult('Erreur : agent indisponible.');
        } finally {
            setAnalyseLoading(false);
        }
    };

    // ══════════════════════════════════════════════════════════════════════
    // ASSIGNMENT
    // ══════════════════════════════════════════════════════════════════════
    const affSuggest = async () => {
        if (!affMid) return;
        setAffLoading(true); setAffMsg('');
        try {
            const res = await api.post(`/agent/planning/missions/${affMid}/suggest`);
            setAffMsg(res.data?.message || 'Demande envoyée.');
            setTimeout(affRefresh, 1500);
        } catch { setAffMsg('Erreur lors de la suggestion.'); }
        finally { setAffLoading(false); }
    };

    const affRefresh = async () => {
        if (!affMid) return;
        setAffLoading(true);
        try {
            const res = await api.get(`/agent/planning/missions/${affMid}/proposals`);
            setAffProposals(Array.isArray(res.data) ? res.data : []);
        } catch { setAffProposals([]); }
        finally { setAffLoading(false); }
    };

    const affChoose = async (proposalId, chauffeurId) => {
        setAffLoading(true); setAffMsg('');
        try {
            await api.post(`/agent/planning/proposals/${proposalId}/decide?action=CONFIRM`);
            setAffMsg('Affectation confirmée');
            setAffProposals(p => p.filter(x => x.id !== proposalId));
        } catch { setAffMsg('Erreur lors de la confirmation.'); }
        finally { setAffLoading(false); }
    };

    const openAssignConfirm = (missionId, chauffeurId, chauffeurName) => {
        console.log('openAssignConfirm:', { missionId, chauffeurId, chauffeurName });
        setAssignConfirmData({ missionId, chauffeurId, chauffeurName });
        setShowAssignConfirm(true);
    };

    const handleConfirmStepNext = () => {
        if (!assignConfirmData) {
            console.warn('handleConfirmStepNext: No data to assign');
            return;
        }
        console.log('Finalizing reassignment navigation:', assignConfirmData);
        setShowAssignConfirm(false);
        // Navigate to assignment page with chauffeur pre-selected in state
        const targetUrl = `/missions/assign/${assignConfirmData.missionId}`;
        console.log('Navigating to:', targetUrl);
        navigate(targetUrl, { 
            state: { selectedChauffeurId: assignConfirmData.chauffeurId } 
        });
    };

    const affReject = async (proposalId) => {
        setAffLoading(true);
        try {
            await api.post(`/agent/planning/proposals/${proposalId}/decide?action=REJECT`);
            setAffProposals(p => p.filter(x => x.id !== proposalId));
        } catch { }
        finally { setAffLoading(false); }
    };

    // ══════════════════════════════════════════════════════════════════════
    // INCIDENT
    // ══════════════════════════════════════════════════════════════════════
    const fetchIncidents = async () => {
        setIncLoading(true);
        try {
            const r2 = await api.get('/agent/planning/incidents');
            setIncRows(Array.isArray(r2.data) ? r2.data : []);
        } catch { }
        finally { setIncLoading(false); }
    };

    const fetchIncProposals = async (mid) => {
        if (!mid) return;
        setIsInspectionModal(false);
        setSelectedIncMid(mid);
        setShowIncModal(true);
        setIncLoading(true);
        try {
            const res = await api.get(`/agent/planning/context?action=refresh_incident&mid=${mid}`);
            setIncProposals(Array.isArray(res.data) ? res.data : []);
        } catch {
            setIncProposals([]);
        } finally {
            setIncLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 'incident') fetchIncidents();
    }, [tab]);

    // ══════════════════════════════════════════════════════════════════════
    // OCR
    // ══════════════════════════════════════════════════════════════════════
    const ocrStage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setOcrLoading(true); setOcrMsg(''); setOcrStageData(null);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await api.post('/agent/planning/ocr/stage', fd, {
                headers: { 'Content-Type': undefined },
            });
            setOcrStageData(res.data);
            setOcrMsg(res.data?.stage_status || 'Document traité.');
        } catch { setOcrMsg("Erreur OCR. Vérifiez que l'agent tourne."); }
        finally { setOcrLoading(false); }
    };

    const ocrCommit = async () => {
        if (!ocrStageData) return;
        setOcrLoading(true);
        try {
            const res = await api.post('/agent/planning/ocr/commit', ocrStageData);
            setOcrMsg(res.data?.message || 'Enregistré.');
            setOcrStageData(null);
        } catch { setOcrMsg('Erreur commit OCR.'); }
        finally { setOcrLoading(false); }
    };

    // ─── helpers ─────────────────────────────────────────────────────────────
    const statusColor = s => ({
        'EN_ATTENTE': '#fbbf24', 'EN_COURS': '#34d399', 'TERMINE': '#6366f1',
        'ANNULE': '#f87171', 'PLANIFIEE': '#60a5fa',
    }[s] || '#94a3b8');

    const TABS = [
        { id: 'dashboard', label: t('agentCenter.tabs.dashboard') || 'Dashboard', icon: 'dashboard' },
        { id: 'chat', label: t('agentCenter.tabs.chat') || 'Chat IA', icon: 'chat' },
        { id: 'analyse', label: t('agentCenter.tabs.analyse') || 'Analyser', icon: 'analyse' },
        { id: 'assignment', label: t('agentCenter.tabs.assignment') || 'Affectation', icon: 'assignment' },
        { id: 'incident', label: t('agentCenter.tabs.incident') || 'Incidents', icon: 'incident' },
        { id: 'checklists', label: t('agentCenter.tabs.checklists') || 'Contrôles', icon: 'inspection' },
        { id: 'ocr', label: t('agentCenter.tabs.ocr') || 'OCR Permis', icon: 'ocr' },
        { id: 'fraud', label: t('agentCenter.tabs.fraud') || 'Fraude', icon: 'shield-check' },
        { id: 'reports', label: t('agentCenter.tabs.reports') || 'Rapports', icon: 'file-earmark-pdf' },
        { id: 'profitability', label: t('agentCenter.tabs.profitability') || 'Rentabilité', icon: 'cash-stack' },
        { id: 'pricing', label: t('agentCenter.tabs.pricing') || 'Prix IA', icon: 'tag' },
    ];

    // ─── render ──────────────────────────────────────────────────────────────
    return (
        <DashboardLayout>
            <div className="modern-dashboard">
                {/* ── HEADER ── */}
                <div className="dashboard-header mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1 className="display-6 fw-bold text-dark mb-2">Control Center</h1>
                            <p className="text-muted fs-5 mb-0">Assistance logicielle et planification intelligente</p>
                        </div>
                        <div className="header-actions">
                            <div className="d-flex align-items-center gap-3">
                                <span className="acc-live-status">
                                    <span className="acc-live-dot" /> IA en ligne
                                </span>
                                <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={fetchDashboard}>
                                    <Icon name="refresh" /> Rafraîchir
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── TABS ── */}
                <nav className="acc-tab-bar">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            className={`acc-tab ${tab === t.id ? 'active' : ''}`}
                            onClick={() => setTab(t.id)}
                        >
                            <Icon name={t.icon} />
                            <span>{t.label}</span>
                        </button>
                    ))}
                </nav>

                {/* ── CONTENT ── */}
                <main className="acc-body">

                    {/* ========== DASHBOARD ========== */}
                    {tab === 'dashboard' && (
                        <div className="fade-in">
                            {dashLoading ? <div className="acc-loading spinning"><Icon name="spinner" /> Chargement des données…</div> : (
                                <>
                                    {/* Stats Highlights */}
                                    <div className="row g-3 mb-4">
                                        <div className="col-md-6 col-xl-3">
                                            <div className="acc-card stats-primary">
                                                <div className="acc-card-title">
                                                    <Icon name="assignment" /> Missions Actives
                                                </div>
                                                <div className="d-flex justify-content-between align-items-end">
                                                    <h2 className="display-4 fw-bold mb-0">{missions.length}</h2>
                                                    <div className="fs-1 opacity-50"><Icon name="mission" /></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6 col-xl-3">
                                            <div className="acc-card stats-accent">
                                                <div className="acc-card-title">
                                                    <Icon name="chat" /> Messages IA
                                                </div>
                                                <div className="d-flex justify-content-between align-items-end">
                                                    <h2 className="display-4 fw-bold mb-0">{chatHistory.length}</h2>
                                                    <div className="fs-1 opacity-50"><Icon name="chat" /></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6 col-xl-3">
                                            <div className="acc-card">
                                                <div className="acc-card-title text-muted">
                                                    <Icon name="ocr" /> Documents Scannés
                                                </div>
                                                <div className="d-flex justify-content-between align-items-end">
                                                    <h2 className="display-4 fw-bold mb-0">{ocrStageData ? 1 : 0}</h2>
                                                    <div className="fs-1 text-primary-light opacity-25"><Icon name="ocr" /></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6 col-xl-3">
                                            <div className="acc-card">
                                                <div className="acc-card-title text-muted">
                                                    <Icon name="incident" /> Incidents Actifs
                                                </div>
                                                <div className="d-flex justify-content-between align-items-end">
                                                    <h2 className="display-4 fw-bold mb-0 text-danger">{requests.length}</h2>
                                                    <div className="fs-1 text-danger opacity-25"><Icon name="incident" /></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row g-3">
                                        {/* Summary / Recent Activity */}
                                        <div className="col-xl-8">
                                            <div className="acc-card h-100">
                                                <div className="acc-card-title">
                                                    <Icon name="mission" /> Missions en cours d'exploitation
                                                </div>
                                                {missions.length === 0 ? (
                                                    <div className="text-center py-5 text-muted">Aucune mission disponible.</div>
                                                ) : (
                                                    <div className="acc-table-wrap">
                                                        <table className="acc-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Référence</th>
                                                                    <th>Trajet</th>
                                                                    <th>Statut</th>
                                                                    <th>Départ</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {missions.map((m, i) => (
                                                                    <tr key={m.id || i}>
                                                                        <td><span className="acc-mono">{m.referenceNumber || m.id || '—'}</span></td>
                                                                        <td className="fw-bold">{m.departureLocation || '?'} → {m.arrivalLocation || '?'}</td>
                                                                        <td><span className="acc-status-pill" style={{ background: statusColor(m.status) + '22', color: statusColor(m.status), borderColor: statusColor(m.status) + '55' }}>{m.status}</span></td>
                                                                        <td className="acc-muted">{m.departureDate ? new Date(m.departureDate).toLocaleDateString('fr-FR') : '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Pending Decisions */}
                                        <div className="col-xl-4">
                                            <div className="acc-card h-100">
                                                <div className="acc-card-title">
                                                    <Icon name="warning" /> Décisions IA en attente
                                                </div>
                                                {requests.length === 0 ? (
                                                    <div className="text-center py-5 text-muted">Tout est sous contrôle.</div>
                                                ) : (
                                                    <div className="acc-proposals-list mt-3">
                                                        {requests.slice(0, 5).map((r, i) => (
                                                            <div key={r.id || i} className="acc-proposal-row p-3 mb-3 border rounded-3 bg-light">
                                                                <div className="acc-proposal-info mb-3">
                                                                    <div className="fw-bold mb-1">Mission {r.missionId}</div>
                                                                    <div className="small text-muted mb-2">Chauffeur: {getDriverLabel(r)}</div>
                                                                </div>
                                                                <div className="acc-proposal-btns d-flex gap-2">
                                                                    <button className="acc-btn-ok btn-sm flex-fill" onClick={() => affChoose(r.id, r.chauffeurId)}>Accepter</button>
                                                                    <button className="acc-btn-ko btn-sm flex-fill" onClick={() => affReject(r.id)}>Refuser</button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {requests.length > 5 && (
                                                            <button className="btn btn-link btn-sm w-100 mt-2" onClick={() => setTab('assignment')}>Voir tout</button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ========== CHAT IA ========== */}
                    {tab === 'chat' && (
                        <div className="acc-card acc-chat-layout fade-in">
                            <div className="acc-card-title border-bottom pb-4 mb-4">
                                <div>
                                    <h2 className="h4 fw-bold mb-1"><Icon name="chat" /> Chat avec l'Assistant IA</h2>
                                    <p className="text-muted small mb-0">Posez n'importe quelle question sur vos missions ou l'état du réseau.</p>
                                </div>
                            </div>

                            {/* Mission context selector */}
                            <div className="acc-chat-context mb-4">
                                <label className="small fw-bold text-muted mb-2 d-block">CONTEXTE MISSION</label>
                                <select className="form-select form-select-sm rounded-pill" value={chatMid} onChange={e => setChatMid(e.target.value)}>
                                    <option value="">— Général —</option>
                                    {missions.map(m => (
                                        <option key={m.id || m.referenceNumber} value={m.id || m.referenceNumber}>
                                            {getMissionLabel(m)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Messages */}
                            <div className="acc-chat-messages rounded-4 shadow-inner mb-4">
                                {chatHistory.length === 0 && (
                                    <div className="acc-chat-welcome py-5 text-center">
                                        <div className="display-1 text-primary opacity-25 mb-3"><Icon name="chat" /></div>
                                        <h4 className="fw-bold">Prêt à vous aider</h4>
                                        <p className="text-muted">Analyse des risques, retards, ou disponibilité...</p>
                                        <div className="acc-suggestions d-flex flex-wrap justify-content-center gap-2 mt-4">
                                            {['Quelles missions sont en retard ?', 'Qui est disponible aujourd\'hui ?', 'Analyse les risques de la flotte'].map(s => (
                                                <button key={s} className="btn btn-light btn-sm rounded-pill border shadow-sm px-3" onClick={() => { setChatQuery(s); }}>
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {chatHistory.map((m, i) => (
                                    <div key={i} className={`acc-msg ${m.role}`}>
                                        <div className="acc-msg-bubble shadow-sm">{m.text}</div>
                                    </div>
                                ))}
                                {chatLoading && (
                                    <div className="acc-msg agent">
                                        <div className="acc-msg-bubble acc-typing"><span /><span /><span /></div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Input */}
                            <form className="acc-chat-form mt-auto" onSubmit={sendChat}>
                                <div className="input-group shadow-sm rounded-pill overflow-hidden border">
                                    <input
                                        className="form-control border-0 px-4"
                                        value={chatQuery}
                                        onChange={e => setChatQuery(e.target.value)}
                                        placeholder="Posez votre question à l'IA…"
                                        disabled={chatLoading}
                                    />
                                    <button type="submit" className="btn btn-primary px-4" disabled={chatLoading || !chatQuery.trim()}>
                                        <Icon name="send" />
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* ========== ANALYSE ========== */}
                    {tab === 'analyse' && (
                        <div className="acc-card fade-in">
                            <div className="acc-card-title border-bottom pb-4 mb-4">
                                <div>
                                    <h2 className="h4 fw-bold mb-1"><Icon name="analyse" /> Analyse IA de Mission</h2>
                                    <p className="text-muted small mb-0">Risques, ressources et état prévisionnel d'une mission spécifique.</p>
                                </div>
                            </div>

                            <div className="row g-4 mb-4">
                                <div className="col-md-8">
                                    <div className="acc-field-group mb-0">
                                        <label className="small fw-bold text-muted mb-2 d-block">MISSION À ANALYSER</label>
                                        <select className="form-select rounded-pill" value={analyseMid} onChange={e => setAnalyseMid(e.target.value)}>
                                            <option value="">— Choisissez une mission —</option>
                                            {missions.map(m => (
                                                <option key={m.id || m.referenceNumber} value={m.id || m.referenceNumber}>
                                                    {getMissionLabel(m)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-4 d-flex align-items-end">
                                    <button className="acc-btn-primary w-100" onClick={runAnalyse} disabled={!analyseMid || analyseLoading}>
                                        {analyseLoading ? <><Icon name="spinner" /> Analyse…</> : <><Icon name="analyse" /> Lancer l'analyse</>}
                                    </button>
                                </div>
                            </div>

                            {analyseResult && (
                                <div className="acc-analyse-result rounded-4 overflow-hidden border mt-4">
                                    <div className="acc-analyse-result-header bg-light p-3 border-bottom fw-bold text-primary">
                                        <Icon name="lightbulb" /> Rapport d'analyse système
                                    </div>
                                    <div className="acc-analyse-text bg-white p-4" style={{ fontSize: '1rem', lineHeight: '1.7' }}>{analyseResult}</div>
                                </div>
                            )}
                            {!analyseResult && !analyseLoading && (
                                <div className="acc-empty-large text-center py-5 bg-light rounded-4 border border-dashed mt-4">
                                    <Icon name="analyse" />
                                    <p className="mb-0 mt-2 text-muted">Sélectionnez une mission pour commencer l'analyse prédictive.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== ASSIGNMENT ========== */}
                    {tab === 'assignment' && (
                        <div className="acc-card fade-in">
                            <div className="acc-card-title border-bottom pb-4 mb-4">
                                <div>
                                    <h2 className="h4 fw-bold mb-1"><Icon name="assignment" /> Affectation Automatique</h2>
                                    <p className="text-muted small mb-0">L'IA suggère le chauffeur optimal. Validez ou ajustez les propositions.</p>
                                </div>
                            </div>

                            <div className="row g-4 align-items-end mb-4">
                                <div className="col-md-6">
                                    <label className="small fw-bold text-muted mb-2 d-block">SÉLECTIONNEZ UNE MISSION</label>
                                    <select className="form-select rounded-pill" value={affMid} onChange={e => setAffMid(e.target.value)}>
                                        <option value="">— Choisissez —</option>
                                        {missions.map(m => (
                                            <option key={m.id || m.referenceNumber} value={m.id || m.referenceNumber}>
                                                {getMissionLabel(m)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 d-flex gap-2">
                                    <button className="acc-btn-primary flex-fill" onClick={affSuggest} disabled={!affMid || affLoading}>
                                        <Icon name="compute" /> Suggérer
                                    </button>
                                    <button className="acc-btn-secondary px-3" onClick={affRefresh} disabled={affLoading}>
                                        <Icon name="refresh" />
                                    </button>
                                </div>
                            </div>

                            {affMsg && <div className="acc-msg-banner mb-4 border-primary bg-primary-soft text-primary">{affMsg}</div>}

                            {affLoading && <div className="acc-loading py-5 text-center spinning"><Icon name="spinner" /> Calcul des optimisations…</div>}

                            {affProposals.length > 0 ? (
                                <div className="acc-proposals-full">
                                    <div className="acc-card-title small text-muted mb-4">
                                        {affProposals.length} PROPOSITION(S) DISPONIBLE(S)
                                    </div>
                                    <div className="row g-4">
                                        {affProposals.map((p, i) => {
                                            const confidence = p.confidenceScore || (p.score_global ? Math.round(p.score_global * 100) : null);
                                            return (
                                                <div key={p.id || i} className="col-lg-6">
                                                    <div className="acc-proposal-card h-100 p-4 border rounded-4 shadow-sm bg-white">
                                                        <div className="d-flex justify-content-between align-items-start mb-4">
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="acc-driver-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '2.5rem', height: '2.5rem' }}>
                                                                    <Icon name="driver" />
                                                                </div>
                                                                <div>
                                                                    <div className="fw-bold fs-5">{getDriverLabel(p)}</div>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <div className="small text-muted">{p.exploitant_nom || 'Interne'}</div>
                                                                        {p.phone_number && (
                                                                            <div className="text-primary small d-flex align-items-center gap-1">
                                                                                <Icon name="telephone" />
                                                                                <span>{p.phone_number}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {confidence !== null && (
                                                                <div className="text-end">
                                                                    <div className="h4 fw-bold text-primary mb-0">{confidence}%</div>
                                                                    <div className="small text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Confiance</div>
                                                                </div>
                                                            )}
                                                        </div>


                                                        <div className="d-flex gap-2">
                                                            <button className="acc-btn-ok flex-fill" onClick={() => openAssignConfirm(p.missionId, p.chauffeurId, p.chauffeur_nom || p.fullName || p.chauffeurId)}>Confirmer</button>
                                                            <button className="acc-btn-ko px-3" onClick={() => affReject(p.id)}><Icon name="cross" /></button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : !affLoading && (
                                <div className="acc-empty-large text-center py-5 bg-light rounded-4 border border-dashed">
                                    <div className="display-4 text-muted opacity-25 mb-3"><Icon name="compute" /></div>
                                    <p className="mb-0 text-muted">Awaiting mission selection for AI assignment suggestions.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== CHECKLISTS ========== */}
                    {tab === 'checklists' && (
                        <div className="acc-card fade-in">
                            <div className="acc-card-title border-bottom pb-4 mb-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h2 className="h4 fw-bold mb-1"><Icon name="inspection" /> {t('agentCenter.checklists.title')}</h2>
                                        <p className="text-muted small mb-0">{t('agentCenter.checklists.subtitle')}</p>
                                    </div>
                                    <button className="btn btn-outline-secondary btn-sm" onClick={fetchDashboard}>
                                        <Icon name="refresh" /> {t('agentCenter.refresh')}
                                    </button>
                                </div>
                            </div>

                            {dashLoading ? <div className="acc-loading spinning py-5"><Icon name="spinner" /> {t('agentCenter.dashboard.loading')}</div> : (
                                checklists.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <div className="display-4 mb-3 opacity-25"><Icon name="inspection" /></div>
                                        {t('agentCenter.checklists.noChecklists')}
                                    </div>
                                ) : (
                                    <div className="acc-table-wrap mt-2">
                                        <table className="acc-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('agentCenter.checklists.mission')}</th>
                                                    <th>{t('agentCenter.checklists.inspectedOn')}</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {checklists.map((c, i) => {
                                                    const m = missions.find(x => x.id === c.missionId);
                                                    return (
                                                        <tr key={c.id || i}>
                                                            <td className="fw-bold">{m ? `${m.departureLocation || ''} ➔ ${m.arrivalLocation || ''}` : `Mission ${c.missionId || '—'}`}</td>
                                                            <td>{c.inspectedAt ? new Date(c.inspectedAt).toLocaleString(t('locale') === 'en' ? 'en-US' : 'fr-FR') : '—'}</td>
                                                            <td className="text-end">
                                                                <button 
                                                                    className="btn btn-sm btn-outline-success rounded-pill"
                                                                    onClick={() => runInspectionAnalysis(c.missionId, c.chauffeurId)}
                                                                >
                                                                    <Icon name="analyse" /> {t('agentCenter.checklists.analyzeWithAI')}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            )}
                        </div>
                    )}

                    {/* ========== INCIDENT ========== */}
                    {tab === 'incident' && (
                        <div className="acc-card fade-in">
                            <div className="acc-card-title border-bottom pb-4 mb-4 d-flex justify-content-between align-items-center">
                                <div>
                                    <h2 className="h4 fw-bold mb-1"><Icon name="incident" /> {t('agentCenter.incidents.title')}</h2>
                                    <p className="text-muted small mb-0">{t('agentCenter.incidents.subtitle')}</p>
                                </div>
                                <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={fetchIncidents} disabled={incLoading}>
                                    <Icon name={incLoading ? 'spinner' : 'refresh'} /> {t('agentCenter.refresh')}
                                </button>
                            </div>

                            <div className="row g-5">
                                <div className="col-12">
                                    <div className="p-4 rounded-4 border bg-light shadow-sm">
                                        {incRows.length === 0 ? (
                                            <div className="text-center py-5 text-muted fst-italic">{t('agentCenter.incidents.noIncidents')}</div>
                                        ) : (
                                            incRows.map((r, i) => (
                                                <div key={i} className="d-flex align-items-center justify-content-between p-4 mb-3 bg-white rounded-3 border-start border-4 border-danger shadow-sm">
                                                    <div className="flex-grow-1">
                                                        <div className="fw-bold fs-5 mb-1 d-flex gap-2 align-items-center">
                                                            {getMissionRoute(r.missionId || r.mid)}
                                                            {r.status && <span className="badge bg-secondary">{r.status}</span>}
                                                        </div>
                                                        <div className="text-muted mb-2">{r.note || r.description || 'Description non spécifiée.'}</div>
                                                    </div>
                                                    <div className="text-end d-flex flex-column align-items-end gap-2">
                                                        <span className="badge rounded-pill bg-danger-soft text-danger px-3 py-2 fs-6 mb-1 d-inline-block">
                                                            {r.category || r.type || 'Alerte'}
                                                        </span>
                                                        <div className="small text-muted mb-2">{r.date || r.createdAt ? new Date(r.date || r.createdAt).toLocaleString('fr-FR') : ''}</div>
                                                        <button 
                                                            className="btn btn-primary btn-sm rounded-pill px-3 d-flex align-items-center gap-2"
                                                            onClick={() => fetchIncProposals(r.missionId || r.mid)}
                                                        >
                                                            <Icon name="compute" /> {t('agentCenter.incidents.manageWithAI')}
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ========== OCR PERMIS ========== */}
                    {tab === 'ocr' && (
                        <div className="acc-card fade-in">
                            <div className="acc-card-title border-bottom pb-4 mb-4">
                                <div>
                                    <h2 className="h4 fw-bold mb-1"><Icon name="ocr" /> {t('agentCenter.ocr.title')}</h2>
                                    <p className="text-muted small mb-0">{t('agentCenter.ocr.subtitle')}</p>
                                </div>
                            </div>

                            {!ocrStageData ? (
                                <div className="acc-ocr-modern-dropzone py-5 text-center rounded-4 border-dashed border-2 bg-light shadow-inner clickable" style={{ cursor: 'pointer' }}>
                                    <input type="file" id="ocrFile" style={{ display: 'none' }} accept="image/*,.pdf" onChange={ocrStage} disabled={ocrLoading} />
                                    <label htmlFor="ocrFile" className="w-100 h-100 m-0 cursor-pointer">
                                        {ocrLoading ? (
                                            <>
                                                <div className="display-1 text-primary mb-3 spinning"><Icon name="spinner" /></div>
                                                <h4 className="fw-bold">Traitement IA...</h4>
                                                <p className="text-muted">Analyse en cours, veuillez patienter</p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="display-1 text-primary opacity-25 mb-3"><Icon name="upload" /></div>
                                                <h4 className="fw-bold">{t('agentCenter.ocr.dropOrClick')}</h4>
                                                <p className="text-muted mb-0">Cliquez ou glissez-déposez une image ou un PDF ici</p>
                                            </>
                                        )}
                                    </label>
                                </div>
                            ) : (
                                <div className="p-4 rounded-4 border bg-white shadow-sm">
                                    <div className="acc-alert bg-success-soft text-success p-3 rounded-3 mb-4 d-flex align-items-center gap-3">
                                        <Icon name="check" /> <span className="fw-bold">Données extraites avec succès.</span> Veuillez les vérifier avant validation.
                                    </div>
                                    <div className="row g-4">
                                        <div className="col-md-4">
                                            <label className="small fw-bold text-muted mb-2 d-block">NOM</label>
                                            <input className="form-control rounded-pill" value={ocrStageData.lastName || ocrStageData.last_name || ''} onChange={e => setOcrStageData({ ...ocrStageData, lastName: e.target.value, last_name: e.target.value })} />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="small fw-bold text-muted mb-2 d-block">PRÉNOM</label>
                                            <input className="form-control rounded-pill" value={ocrStageData.firstName || ocrStageData.first_name || ''} onChange={e => setOcrStageData({ ...ocrStageData, firstName: e.target.value, first_name: e.target.value })} />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="small fw-bold text-muted mb-2 d-block">N° PERMIS</label>
                                            <input className="form-control rounded-pill" value={ocrStageData.permitNumber || ocrStageData.permit_number || ''} onChange={e => setOcrStageData({ ...ocrStageData, permitNumber: e.target.value, permit_number: e.target.value })} />
                                        </div>
                                        {(ocrStageData.ocrText || ocrStageData.ocr_text) && (
                                            <div className="col-12 mt-4">
                                                <label className="small fw-bold text-muted mb-2 d-block">TEXTE BRUT (EXTRAIT IA)</label>
                                                <textarea className="form-control rounded-4 bg-light" rows={4} readOnly value={ocrStageData.ocrText || ocrStageData.ocr_text} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex gap-3 mt-5 pt-4 border-top">
                                        <button className="acc-btn-primary flex-fill" onClick={ocrCommit} disabled={ocrLoading}>
                                            <Icon name="check" /> Valider l'enregistrement
                                        </button>
                                        <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => { setOcrStageData(null); setOcrMsg(''); }}>
                                            Recommencer
                                        </button>
                                    </div>
                                </div>
                            )}
                            {ocrMsg && <div className="acc-msg-banner mt-4 border-info bg-info-soft text-info">{ocrMsg}</div>}
                        </div>
                    )}

                    {/* ========== NEW TABS ========== */}
                    {tab === 'fraud' && <FraudTab />}
                    {tab === 'reports' && <ReportsTab />}
                    {tab === 'profitability' && <ProfitabilityTab />}
                    {tab === 'pricing' && <AiPricingTab />}
                </main>
            </div>

            {/* ── PREMIUM MODAL (AI Suggestions for Incident) ── */}
            {showIncModal && (
                <div className="modal d-block acc-modal-premium" onClick={() => setShowIncModal(false)}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="modal-content shadow-lg border-0 overflow-hidden">
                            <div className={`modal-header ${isInspectionModal ? 'bg-success' : 'bg-danger'} text-white border-0 py-3 px-4`}>
                                <div className="d-flex align-items-center gap-2">
                                    <Icon name={isInspectionModal ? 'inspection' : 'incident'} />
                                    <h5 className="modal-title fw-bold">
                                        {isInspectionModal ? t('agentCenter.modals.inspectionReport') : t('agentCenter.modals.incidentProposals')} 
                                        {selectedIncMid && ` — ${getMissionRoute(selectedIncMid)}`}
                                    </h5>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <button 
                                        className="btn btn-sm btn-outline-light d-flex align-items-center justify-content-center px-3 gap-2" 
                                        onClick={() => isInspectionModal ? runInspectionAnalysis(selectedIncMid) : fetchIncProposals(selectedIncMid)} 
                                        disabled={incLoading}
                                        title={t('agentCenter.modals.refresh')}
                                    >
                                        <Icon name="refresh" />
                                        <span>{t('agentCenter.modals.refresh')}</span>
                                    </button>
                                    <button type="button" className="btn-close btn-close-white ms-2" onClick={() => setShowIncModal(false)}></button>
                                </div>
                            </div>
                            <div className="modal-body p-4 bg-light">
                                {incLoading ? (
                                    <div className="text-center py-5">
                                        <div className={`spinner-border ${isInspectionModal ? 'text-success' : 'text-danger'} mb-3`} role="status"></div>
                                        <p className="text-muted">{isInspectionModal ? t('agentCenter.modals.analyzingInspection') : t('agentCenter.modals.calculatingReplacement')}</p>
                                    </div>
                                ) : isInspectionModal ? (
                                    <div className="inspection-report-view">
                                        {incProposals[0]?.error ? (
                                            <div className="alert alert-danger rounded-4 p-4 shadow-sm">
                                                <Icon name="warning" /> {incProposals[0].error}
                                            </div>
                                        ) : (
                                            <div className="acc-card bg-white p-4 rounded-4 shadow-sm border-top border-4 border-success">
                                                <div className="d-flex align-items-center gap-3 mb-4">
                                                    <div className="acc-driver-avatar bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '3.5rem', height: '3.5rem' }}>
                                                        <Icon name="inspection" />
                                                    </div>
                                                    <div>
                                                        <h4 className="fw-bold mb-1">{t('agentCenter.modals.inspectionReport')}</h4>
                                                        <span className="badge bg-success-soft text-success">{t('agentCenter.modals.analysisCompleted')}</span>
                                                    </div>
                                                </div>
                                                
                                                <div className="report-content bg-light p-4 rounded-4 shadow-inner" style={{ whiteSpace: 'pre-wrap', fontSize: '1.05rem', lineHeight: '1.6' }}>
                                                    {incProposals[0]?.report || t('agentCenter.modals.generatingReport')}
                                                </div>

                                                <div className="mt-4 d-flex justify-content-end">
                                                    <button className="btn btn-secondary rounded-pill px-4" onClick={() => setShowIncModal(false)}>
                                                        {t('agentCenter.modals.closeReport')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="row g-4">
                                        {incProposals.map((p, idx) => (
                                            <div key={idx} className="col-lg-6">
                                                <div className="acc-proposal-card h-100 p-4 border rounded-4 shadow-sm bg-white border-top border-4 border-danger">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="acc-driver-avatar bg-danger text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                                                                <Icon name="driver" />
                                                            </div>
                                                            <div>
                                                                <div className="fw-bold fs-5 text-dark">{p.chauffeur_identite || p.chauffeur_nom || p.fullName || p.chauffeurId}</div>
                                                                <div className="small text-muted">{t('agentCenter.modals.confidence')} <span className="fw-bold text-danger">{Math.round((p.score_global || 0) * 100)}%</span></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="d-flex gap-2 mt-auto">
                                                        <button 
                                                            className="btn btn-danger flex-fill py-2 fw-bold" 
                                                            onClick={() => {
                                                                openAssignConfirm(p.mission_id || selectedIncMid, p.chauffeur_id || p.chauffeurId, p.chauffeur_identite || p.chauffeur_nom || p.fullName || p.chauffeurId);
                                                                setShowIncModal(false);
                                                            }}
                                                        >
                                                            {t('agentCenter.modals.accept')}
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-secondary px-3" 
                                                            onClick={() => setShowIncModal(false)}
                                                        >
                                                            <Icon name="cross" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* ========== REASSIGNMENT CONFIRMATION MODAL ========== */}
            {showAssignConfirm && (
                <div className="acc-modal-overlay d-flex align-items-center justify-content-center" style={{ 
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    width: '100vw', 
                    height: '100vh', 
                    zIndex: 10000,
                    backdropFilter: 'blur(8px)', 
                    backgroundColor: 'rgba(0,0,0,0.5)' 
                }}>
                    <div className="acc-modal-content bg-white rounded-4 shadow-lg p-5 text-center position-relative" style={{ maxWidth: '500px', width: '90%', borderTop: '6px solid var(--acc-primary)' }}>
                        <button className="btn-close position-absolute top-0 end-0 m-4" onClick={() => setShowAssignConfirm(false)}></button>
                        
                        <div className="display-4 text-primary mb-4">
                            <Icon name="driver" />
                        </div>
                        
                        <h2 className="fw-bold mb-3">{t('agentCenter.assignment.confirmModal.title')}</h2>
                        
                        <p className="fs-5 text-muted mb-4">
                            {t('agentCenter.assignment.confirmModal.message')} <span className="fw-bold text-dark">{assignConfirmData?.chauffeurName}</span>.
                            <br/><span className="badge bg-primary mt-2">{getMissionRoute(assignConfirmData?.missionId)}</span>
                        </p>
                        
                        <div className="acc-alert bg-light border p-3 rounded-3 mb-4 text-start">
                            <div className="d-flex gap-3 align-items-center">
                                <div className="text-warning fs-4"><Icon name="warning" /></div>
                                <div className="small text-muted">{t('agentCenter.assignment.confirmModal.warning')}</div>
                            </div>
                        </div>

                        <button className="btn btn-primary btn-lg rounded-pill px-5 fw-bold w-100 shadow-sm" onClick={handleConfirmStepNext}>
                            {t('agentCenter.assignment.confirmModal.next')} <Icon name="chevron-right" />
                        </button>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default AgentControlCenter;
